import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { clone } from '@helpers/arrays';
import { sharedRegex } from '@helpers/spread/shared-regex';
import { Scenario } from '@models/scenario';
import { PermissionsService } from '@services/auth/permissions.service';
import { ApplicationStatus } from '@services/results/application-status';
import { ResultsStoreService } from '@services/results/results-store.service';
import { ScenarioService } from '@services/scenario/scenario.service';
import { IndividualConfig, Toast, ToastrService } from 'ngx-toastr';
import { NotificationsService } from '@services/notifications/notifications.service';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { DialogAddScenarioComponent } from '../add-scenario-dialog/add-scenario-dialog';
import { ToastLevel } from '@shared/toasterSetup';

const DEFAULT_PROCESSING_MESSAGE = 'Processing';

@Component({
  selector: 'app-scenario-table',
  templateUrl: './scenario-table.component.html',
  styleUrls: ['./scenario-table.component.scss'],
})
export class ScenarioTableComponent implements OnInit {
  private destroy$ = new Subject();
  private scenarioList = [];

  public processingMessage = DEFAULT_PROCESSING_MESSAGE;
  public showProcessing = false;
  userRoleCanEdit: boolean;
  fetching = false;
  scenarioList$ = new Observable<Scenario[]>();
  tableFilter$ = new BehaviorSubject(new ScenarioTableFilter());

  // Form controls
  addScenarioForm: UntypedFormGroup;
  editedScenario = null;
  lastAddedScenario = null;  
  lastUpdatedScenario = null;
  lastDeletedScenario = null;
  lastUndeletedScenario = null;
  copying: boolean = false;

  // Add two new properties to track checkbox state
  public isBaselineChecked = true;
  public isStressChecked = true;

  constructor(
    private scenarioService: ScenarioService,
    private permissions: PermissionsService,
    private store: ResultsStoreService,
    public dialog: MatDialog,
    private readonly notifications: NotificationsService,
    private fb: UntypedFormBuilder
  ) {
    this.userRoleCanEdit = this.permissions.getPermission('ModelStructureScenarios') === 'W';
  }

  ngOnInit(): void {
    const scenarios$ = this.scenarioService.scenarioList$.pipe(
      map(scenarios => scenarios.filter(s => s.record_status === 'A')),
      tap(scenarios => (this.scenarioList = scenarios))
    );

    this.scenarioList$ = combineLatest([scenarios$, this.tableFilter$]).pipe(
      distinctUntilChanged(),
      map(([scenarios, scenarioFilters]) => this.getFilteredScenarios(scenarios, scenarioFilters)),
      takeUntil(this.destroy$)
    );

    // Initialize both checkboxes as checked
    this.isBaselineChecked = true;
    this.isStressChecked = true;
    this.initListeners();
  }

  public onDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getFilteredScenarios(scenarios: Scenario[], scenarioFilters: ScenarioTableFilter): Scenario[] {
    if (scenarioFilters.all !== '') {
      return scenarios.filter(
        scenario =>
          scenario.scenario_name.toLocaleLowerCase().includes(scenarioFilters.all) ||
          scenario.mnemonic.toLocaleLowerCase().includes(scenarioFilters.all) ||
          scenario.scenario_description.toLocaleLowerCase().includes(scenarioFilters.all) ||
          (scenarioFilters.all.toLocaleLowerCase() === 'baseline' && scenario.is_stress === 0) ||
          (scenarioFilters.all.toLocaleLowerCase() === 'stress' && scenario.is_stress === 1)
      );
    }

    // Multi-select filter for is_stress
    let filterByType = false;
    if (this.isBaselineChecked && !this.isStressChecked) {
      scenarios = scenarios.filter(scenario => scenario.is_stress === 0);
      filterByType = true;
    } else if (!this.isBaselineChecked && this.isStressChecked) {
      scenarios = scenarios.filter(scenario => scenario.is_stress === 1);
      filterByType = true;
    } else if (!this.isBaselineChecked && !this.isStressChecked) {
      scenarios = [];
      filterByType = true;
    }
    // If both are checked, show all (no filter by type)

    for (const propertyName of Object.keys(scenarioFilters)) {
      if (scenarioFilters[propertyName] !== '' && !['all', 'is_stress'].includes(propertyName)) {
        scenarios = scenarios.filter(scenario => scenario[propertyName].toString().toLowerCase().includes(scenarioFilters[propertyName]));
      }
    }

    return scenarios;
  }

  onChangeFilter(event: Event, field: string = ''): void {
    const currentFilter = this.tableFilter$.getValue();
    if (field === 'is_stress') {
      const checkbox = event.target as HTMLInputElement;
      if (checkbox.value === '0') {
        this.isBaselineChecked = checkbox.checked;
      } else if (checkbox.value === '1') {
        this.isStressChecked = checkbox.checked;
      }
      // No need to update currentFilter.is_stress, filtering is handled by the two booleans
    } else {
      const value = (event.target as HTMLInputElement).value || '';
      currentFilter[field] = value.toLowerCase();
    }
    this.tableFilter$.next({ ...currentFilter });
  }

  public deleteScenarioBtnClicked(scenario) {
    console.log('Delete button clicked for scenario:', scenario);
    console.log('SweetAlert2 available:', typeof Swal !== 'undefined');
    
    this.openDialogDeleteConfirmation().then(result => {
      console.log('Dialog result:', result);
      if (result.value) {
        console.log('User confirmed deletion, calling onDelete');
        return this.onDelete(scenario);
      } else {
        console.log('User cancelled deletion');
      }
    }).catch(error => {
      console.error('Error showing confirmation dialog:', error);
    });
  }

  private initListeners() {
    // Application Status subscription to hide the "processing" message
    this.initProcessingMessageSubscription();
  }

  private initProcessingMessageSubscription() {
    this.store.appStatus$
      .pipe(
        takeUntil(this.destroy$),
        filter(status => status === ApplicationStatus.ScenariosModified || status === ApplicationStatus.DoneFetchingResults)
      )
      .subscribe(status => {
        if (status === ApplicationStatus.DoneFetchingResults) {
          this.showProcessing = false;
          this.processingMessage = DEFAULT_PROCESSING_MESSAGE;

          if (this.lastDeletedScenario && this.lastDeletedScenario.mnemonic) this.showToastScenarioDeleted();
        }
      });
  }

  createAddScenarioForm(scenarioId = null) {
    // const mnemonicRegex = /^[a-zA-Z0-9-_]+$/;
    const mnemonicRegex = sharedRegex.mnemonic;
    this.addScenarioForm = this.fb.group({
      scenario_name: ['', Validators.required],
      mnemonic: ['', Validators.compose([Validators.required, Validators.pattern(mnemonicRegex), this.uniqueMnemonicValidator()])],
      scenario_description: '',
      is_stress: [{ value: null, disabled: Boolean(scenarioId) }, Validators.required],
    });    
  }

  uniqueMnemonicValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value ? control.value : '';

      if (this.scenarioList) {
        let scenarioMnemonics: string[] = [];
        const currentMnemonic = this.editedScenario ? this.editedScenario.mnemonic.toLocaleLowerCase() : '';
        // Unique set of mnemonics
        let activeScenarios: Scenario[] = this.scenarioList.filter(scenario => scenario.record_status=='A');
        
        scenarioMnemonics = [...Array.from(new Set(activeScenarios.map(scenario => scenario.mnemonic.toLowerCase())))];

        // Mnemonic is forbidden if found in list and different from original mnemonic (when editing)
        const forbidden = scenarioMnemonics.includes(value.toLowerCase()) && (this.copying || currentMnemonic !== value.toLowerCase());
        return forbidden ? { uniqueMnemonic: { value: control.value } } : null;
      }
      return null;
    };
  }

  private showToastScenarioUndeleted() {
    const toast : Toast = {
        title:'Scenario Undeleted',
        message: `Undeleted Scenario ${this.lastUndeletedScenario ? this.lastUndeletedScenario.mnemonic.toUpperCase() : ''}`
    } as Toast;
    this.notifications.showToast(
      ToastLevel.Success,
      toast
    );
  }

  showToastScenarioAdded() {
    const type = 'success';
    const title = 'Scenario Added';
    const text = `Added Scenario ${this.lastAddedScenario.mnemonic.toUpperCase() || ''}`;

    const body = `
      <p class="text-light">${text.replace(/\n/g, '<br>')}</p>
    `;

    const toast: Toast = {
      title :title,
      message: body
      //timeout: 5000,      
    } as Toast;
    this.notifications.showToast(ToastLevel.Success, toast);
  }

  private openDialogDeleteConfirmation() {
    console.log('Opening SweetAlert2 confirmation dialog');
    try {
      const result = Swal.fire({
        title: 'Confirm Deletion',
        text: `Deleting this scenario will result in the removal of all the changes by the user that have not been saved yet. Do you wish to Proceed?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Proceed',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        allowEscapeKey: true,
        allowOutsideClick: false,
        backdrop: true,
        focusConfirm: false,
      } as SweetAlertOptions);
      console.log('SweetAlert2 fire() called, result:', result);
      return result;
    } catch (error) {
      console.error('Error in openDialogDeleteConfirmation:', error);
      throw error;
    }
  }

  openDialog(scenarioId = null, copying = false): void {
    this.copying = copying;
    this.createAddScenarioForm(scenarioId);
    if (scenarioId || copying) {
      const scen = this.scenarioList.find(s => s.scenario_id === scenarioId);
      if (scen) {
        const { scenario_name, mnemonic, scenario_description, is_stress } = scen;
        this.addScenarioForm.setValue({ scenario_name, mnemonic, scenario_description, is_stress });
        if(copying) { this.addScenarioForm.markAllAsTouched(); }
      } else {
        console.error(`Couldn't find scenario Id ${scenarioId}`);
      }
    } else {
      this.resetForm();
    }

    const dialogRef = this.dialog.open(DialogAddScenarioComponent, {
      width: 'calc(100% - 3*140px)', // 140px is the main dialog margin
      height: '460px',
      minWidth: '500px',
      minHeight: '300px',
      maxWidth: '650px',
      maxHeight: '500px',
      data: {
        scenario_id: scenarioId,
        copying: copying,
        form: this.addScenarioForm,
      },
    });

    // Adaptation for KendoUI popup of the canDeactivate Guard
    // when user leaves without saving changes

    dialogRef.afterClosed().subscribe(dialogValue => {
      if (dialogValue) {
        if (dialogValue.result === 'added') {
          this.addScenario(dialogValue.scenario);
        } else if (dialogValue.result === 'updated') {
          this.updateScenario(dialogValue.scenario);
        } else if (dialogValue.result === 'copied') {
          this.copyScenario(dialogValue.scenario);          
        } else if (dialogValue.result === 'cancel') {
          // console.log(`Dialog cancelled`);
        }
        this.editedScenario = null;
      }
    });
  }

  onEdit(scenario: Scenario) {
    this.editedScenario = scenario;
    this.openDialog(scenario.scenario_id);
  }

  onCopy(scenario: Scenario) {
    this.editedScenario = scenario;
    this.openDialog(scenario.scenario_id,true);
  }

  onDelete(scenario: Scenario) {
    if (scenario) {
      this.showProcessing = true;
      this.scenarioService
        .deleteScenario(scenario.scenario_id)
        .toPromise()
        .then(
          deletedScenario => {
            this.lastDeletedScenario = clone(scenario);
            this.resetForm();
            this.getScenarioList();
            this.processingMessage = 'Refreshing active resultsets';
          },
          err => {
            console.warn(`Error deleting Scenario`, scenario, err);
          }
        );
    }
  }

  onUndelete(scenario: Scenario) {
    if (scenario) {
      this.showProcessing = true;
      this.scenarioService
        .undeleteScenario(scenario.scenario_id)
        .toPromise()
        .then(undeletedScenario => {
          this.lastUndeletedScenario = clone(undeletedScenario);
          this.resetForm();
          this.getScenarioList();
          if (this.lastUndeletedScenario && this.lastUndeletedScenario.mnemonic) this.showToastScenarioUndeleted();
          this.processingMessage = 'Refreshing active resultsets';
        });
    }
  }

  addScenario(scenario: Scenario) {
    if (scenario) {
      this.showProcessing = true;
      this.scenarioService
        .addScenario(scenario)
        .toPromise()
        .then((addedScenario: string) => {
          this.lastAddedScenario = clone(addedScenario);
          this.resetForm();
          this.getScenarioList();
          if (this.lastAddedScenario && this.lastAddedScenario.mnemonic) this.showToastScenarioAdded();
          this.showProcessing = false;
        });
    }
  }

  copyScenario(scenario: Scenario) {
    if (scenario) {
      this.showProcessing = true;
      this.scenarioService
        .copyScenario(scenario)
        .toPromise()
        .then((addedScenario: string) => {
          this.lastAddedScenario = clone(addedScenario);
          this.resetForm();
          this.getScenarioList();
          if (this.lastAddedScenario && this.lastAddedScenario.mnemonic) this.showToastScenarioAdded();
          this.showProcessing = false;
        });
    }
  }

  updateScenario(scenario: Scenario) {
    if (scenario) {
      this.showProcessing = true;
      scenario.is_stress = <any>scenario.is_stress === 'Baseline' ? 0 : 1;
      this.scenarioService
        .updateScenario(scenario)
        .toPromise()
        .then(updatedScenario => {
          this.lastUpdatedScenario = clone(updatedScenario);
          this.resetForm();
          this.getScenarioList();
          if (this.lastUpdatedScenario && this.lastUpdatedScenario.mnemonic) this.showToastScenarioUpdated();
          this.showProcessing = false;
        });
    }
  }

  private showToastScenarioUpdated() {
    this.notifications.showToast(ToastLevel.Success, 
      {title:'Scenario Updated', message:`Updated Scenario ${this.lastUpdatedScenario.mnemonic.toUpperCase() || ''}`} as Toast
      //,[]
      );
  }

  private showToastScenarioDeleted() {
    this.notifications.showToast(ToastLevel.Success, 
      {title : 'Scenario Deleted', message: `Deleted Scenario ${this.lastDeletedScenario.mnemonic.toUpperCase() || ''}`} as Toast
    );
    /*
    this.notifications.showToastWithButtons(ToastLevel.Success, 'Scenario Deleted', `Deleted Scenario ${this.lastDeletedScenario.mnemonic.toUpperCase() || ''}`
     ,[
      {
        name: 'UNDO',
        class: 'btn_normal btn_negative btn_white',
        action: this.onUndelete.bind(this, this.lastDeletedScenario),
      },
    ] 
  );
  */
    this.lastDeletedScenario = null;
  }

  resetForm() {
    if (this.addScenarioForm) {
      this.addScenarioForm.reset();
      this.addScenarioForm.patchValue({ is_stress: null, disabled: true });
    }
  }

  private async getScenarioList(): Promise<Scenario[]> {
    return (
      (await this.scenarioService
        .getScenarios()
        .toPromise()
        .catch(err => console.warn('Error getting Scenarios'))) || []
    );
  }
}

export class ScenarioTableFilter {
  scenario_name = '';
  mnemonic = '';
  scenario_description = '';
  is_stress = -1;
  all = '';
}
